import React from 'react';
import GridCountCell from './grid/GridCountCell';

// =============================================================================
// ExportsSummaryPanel
// =============================================================================
// The Exports workspace "Summary" grid: one column per exception category
// (Cutoff, No TT, Equip Not Rel, …, In Per Diem) and one row per urgency band —
// Past Due (date on/before yesterday), Today, Tomorrow. Each PRO lands in the row
// matching when its category-date falls relative to today. Unlike Tracking (urgency
// by column), the urgency here is the row: the Past Due row fills red when a count
// is non-zero; Today/Tomorrow stay plain.
//
// Purely presentational — renders the counts the backend returns and reports cell
// clicks via onSelectCell. Lives inside a <DashboardTile> (which supplies the title
// + drag handle), so it renders bare. Pairs with /lc-dashboards/exports-summary-counts.

export interface ExportsSummaryCol {
  pastdue: number;
  today: number;
  tomorrow: number;
}

export interface ExportsSummaryData {
  cols: { [colKey: string]: ExportsSummaryCol };  // counts keyed by column key (missing = all 0)
}

export interface ExportsSummaryPanelProps {
  data: ExportsSummaryData | null;
  loading: boolean;
  // Fired when a non-zero count is clicked. `col` + `bucket` identify the cell for the
  // drill-down; `title` is a ready-made label for the results header.
  onSelectCell?: (col: string, bucket: string, title: string) => void;
}

// Category columns, in display order. `key` is the backend column key; `label` is the
// header text (wraps to two lines in the narrow header cell).
const COLS: { key: string; label: string }[] = [
  { key: 'cutoff', label: 'Cutoff' },
  { key: 'notraceterminate', label: 'No TT' },
  { key: 'equipnotreleased', label: 'Equip Not Rel' },
  { key: 'yardpull', label: 'Yard Pull' },
  { key: 'deliverby', label: 'Deliver By' },
  { key: 'drop', label: 'Drop' },
  { key: 'live', label: 'Live' },
  { key: 'noproduct', label: 'No Product' },
  { key: 'nodroprelease', label: 'Not Drop Rel' },
  { key: 'droprelease', label: 'Drop Rel' },
  { key: 'perdiem', label: 'In Per Diem' },
];

// Urgency rows. `field` reads the count off a column's counts; `color` is the fill
// applied to a cell only when its count > 0 (Past Due is the only colored band).
const ROWS: { key: keyof ExportsSummaryCol; label: string; color: string }[] = [
  { key: 'pastdue', label: 'Past Due', color: 'bg-red-600 text-white' },
  { key: 'today', label: 'Today', color: '' },
  { key: 'tomorrow', label: 'Tomorrow', color: '' },
];

const EMPTY_COL: ExportsSummaryCol = { pastdue: 0, today: 0, tomorrow: 0 };

const ExportsSummaryPanel: React.FC<ExportsSummaryPanelProps> = ({ data, loading, onSelectCell }) => {
  const placeholder = (value: number) => value === 0 && loading && !data;

  return (
    // w-full so the table fills the tile width (header + cells stretch to the panel
    // edges); natural height keeps the tile's double-click fit-to-content working.
    <div className="w-full bg-panel text-sm">
      <table className="border-collapse w-full">
        <thead>
          <tr className="bg-slate-700 text-white">
            <th className="text-left font-semibold px-2 py-1 min-w-[80px]" />
            {COLS.map((c) => (
              <th key={c.key} className="text-center font-semibold px-2 py-1 w-[60px] align-bottom leading-tight">
                {c.label}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="text-body">
          {ROWS.map((row) => (
            <tr key={row.key} className="border-t border-gray-100 dark:border-gray-700/50">
              <td className="bg-slate-700 text-white text-right font-semibold px-2 py-1 whitespace-nowrap">
                {row.label}
              </td>
              {COLS.map((c) => {
                const value = (data?.cols[c.key] ?? EMPTY_COL)[row.key];
                return (
                  <td key={c.key} className="border-l border-gray-100 dark:border-gray-700/50">
                    <GridCountCell
                      value={value}
                      colorClass={value > 0 ? row.color : ''}
                      placeholder={placeholder(value)}
                      onSelect={onSelectCell ? () => onSelectCell(c.key, row.key, `${c.label} – ${row.label}`) : undefined}
                      title={`View orders for ${c.label} (${row.label})`}
                    />
                  </td>
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ExportsSummaryPanel;
